import { useEffect, useState } from "react";
import CmsBanner from "../../components/Banner/CmsBanner";
import Loader from "../../components/Loader/Loader";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { getFaqsAPI } from "../../api/faq";

export default function FAQAccordion() {
  const [openIndex, setOpenIndex] = useState(0);
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const {
    data: faqs,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["faqs", currentLanguage],
    queryFn: getFaqsAPI,
  });

  const toggleAccordion = (index: number) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  useEffect(() => {
    const body = document.body;
    const handleResize = () => {
      if (window.innerWidth >= 2400) {
        body.classList.add("scale-wrapper");
      } else {
        body.classList.remove("scale-wrapper");
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (isLoading || !faqs?.data) {
    return (
      <div className="wrapper bg-primary-ivory" >
        <CmsBanner classNameShape="hidden" title={t("faq.title")} description={t("faq.description")} />
        <section className="lg:py-[4.167vw] lg:pb-[5.5vw] py-20 2xl:-mt-[2.24vw] -mt-11 z-10">
          <div className="w-full 2xl:max-w-[50vw] max-w-[900px] mx-auto px-4 relative z-20">
            <Loader />
          </div>
        </section>
      </div >
    );
  }

  // Show error state
  if (isError) {
    return (
      <div className="wrapper bg-primary-ivory">
        <CmsBanner
          classNameShape="hidden"
          title="frequently asked questions"
          description="An interactive group game that tests your knowledge and culture"
        />
        <section className="lg:py-20 lg:pb-25 py-10 pb-18 2xl:-mt-[2.24vw] -mt-11 z-10">
          <div className="w-full max-w-[900px] fullhd:max-w-[1100px] mx-auto px-4 relative z-20">
            {/* <ErrorLottie /> */}
            <p className="text-center text-red-600 mt-4">{error?.message || t("faq.failedToLoadFaqs")}</p>
          </div>
        </section>
      </div>
    );
  }

  // Show empty state
  if (faqs?.data?.length === 0) {
    return (
      <div className="wrapper bg-primary-ivory">
        <CmsBanner
          classNameShape="hidden"
          title="frequently asked questions"
          description="An interactive group game that tests your knowledge and culture"
        />
        <section className="lg:py-20 lg:pb-25 py-10 pb-18 bg-white-500 relative top-curve-shape shadow-[0px_0px_74px_0px_#D8AAF0_inset] 2xl:-mt-[2.24vw] -mt-11 z-10">
          <div className="w-full max-w-[900px] 2xl:max-w-[50vw] mx-auto px-4 relative z-20">
            <div className="text-center py-12">
              <svg
                className="w-16 h-16 mx-auto text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900">{t("faq.noFaqsAvailable")}</h3>
              <p className="mt-2 text-black-500">{t("faq.noFaqsAvailableDescription")}</p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="wrapper bg-primary-ivory">
      <CmsBanner classNameShape="hidden" title={t("faq.title")} description={t("faq.description")} />
      <section className="2xl:py-[3.5vw] lg:py-15 py-10 relative z-10 bg-cover bg-center bg-no-repeat bg-primary-ivory">
        <div className="w-full 2xl:max-w-[60vw] max-w-[1040px] mx-auto px-4 relative z-20">
          {faqs?.data?.map((faq: any, index) => (
            <div
              key={faq.id}
              className="w-full lg:rounded-[1.04vw] rounded-2xl 2xl:text-[1.56vw]/[1.2vw] lg:text-lg/tight md:text-base/tight text-sm/tight font-medium lg:border-[0.13vw] border-2 border-primary-orange text-primary-rose bg-primary-ivory 2xl:shadow-[0.313vw_0.313vw_0px_0px_#D76A4D] shadow-[6px_6px_0px_0px_#D76A4D] placeholder:text-black-400 focus:outline-none transition-all lg:mb-[1.2vw] mb-3 last:mb-0 pt-[10px] 2xl:pt-[1vw]"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full flex items-center justify-between text-start transition-colors cursor-pointer lg:px-[2%] px-4 focus:outline-none pb-[10px] 2xl:pb-[1vw]"
                aria-expanded={openIndex === index}
                aria-controls={`faq-answer-${faq.id}`}
              >
                <span className="text-base/tight 2xl:text-[1.56vw] lg:text-lg/tight text-primary-rose font-bold lg:pe-[2%] pe-4 ">
                  {faq.question || t("faq.noQuestionAvailable")}
                </span>

                <span
                  className={`shrink-0 lg:mt-[0.3vw] mt-0.5 transition-transform duration-600 ${openIndex === index ? "rotate-180" : "rotate-0"}`}
                  aria-hidden="true"
                >
                  <svg width="18" height="11" viewBox="0 0 18 11" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-[18px] h-[11px] 2xl:w-[1.17vw] 2xl:h-[0.72vw]">
                    <path d="M17.2638 1.59094L9.71786 9.6085C9.19912 10.1597 8.32318 10.1597 7.80443 9.6085L0.258427 1.59094C-0.30851 0.9885 0.118552 0 0.945802 0H16.5764C17.4037 0 17.8307 0.9885 17.2638 1.59094Z" fill="#6E1E3B" />
                  </svg>

                </span>
              </button>
              <div
                id={`faq-answer-${faq.id}`}
                className={`grid transition-all duration-300 ease-in-out lg:px-[2%] px-4 ${openIndex === index ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
                role="region"
                aria-labelledby={`faq-question-${faq.id}`}
              >
                <div className="overflow-hidden">
                  <p className="text-base/tight 2xl:text-[1.30vw] lg:text-lg/tight text-black-900 font-normal font-rubrik lg:py-[2%] py-4 border-t border-[#97A4F2] pt-[1.56vw]">
                    {faq.answer || t("faq.noAnswerAvailable")}
                  </p>

                  {/* Optional: Show FAQ metadata */}
                  {faq.createdBy && (
                    <div className="text-sm text-gray-700 pt-2 border-t border-gray-200 mt-2">
                      <span>
                        {t("faq.createdBy")}: {faq.createdBy}
                      </span>
                      {faq.createdAt && <span className="ml-4">{new Date(faq.createdAt).toLocaleDateString()}</span>}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
